import React, { useMemo, useState } from 'react';
import { useSelector } from "react-redux";
import { getCurrencySymbol, getOrderTypePermissions, isPermissionAllowed } from 'utils/function';
import { Box, Card, Grid, IconButton, Tooltip, Skeleton, Tabs, Tab } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import PrintIcon from '@mui/icons-material/Print';
import QrCodeIcon from '@mui/icons-material/QrCode';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import MainContainer from 'theme/MainConatiner';
import CustomTypography from 'components/Fileds/CustomTypography';
import CustomButton from 'components/Fileds/CustomButton';
import CustomTextField from 'components/Fileds/CustomTextFiled';
import IOSSwitch from 'components/Fileds/IOSSwitch';
import './style.css';
import theme from 'theme/ThemeContext';

const FeatureToggleSkeleton = () => (
    <Card className="feature-card">
        <Box className="feature-card-content">
            <Box className="feature-info">
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="text" width="90%" height={24} />
            </Box>
            <Skeleton variant="rectangular" width={42} height={26} sx={{ borderRadius: 13 }} />
        </Box>
    </Card>
);

const SettingsSkeleton = () => (
    <Box className="manage-settings-page">
        <Box className="settings-header">
            <Box>
                <Skeleton variant="text" width={200} height={40} />
                <Skeleton variant="text" width={300} height={28} />
            </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, borderBottom: '1px solid #E5E7EB', pb: 1.5, mb: 3, overflowX: 'auto' }}>
            {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} variant="rectangular" width={200} height={40} sx={{ borderRadius: 1 }} />
            ))}
        </Box>

        <Box className="settings-content">
            <Box sx={{ mb: 3 }}>
                <Skeleton variant="text" width={220} height={32} />
                <Skeleton variant="text" width={340} height={20} />
            </Box>
            <Box className="section-wrapper">
                <Card className="settings-section-card">
                    <Grid container spacing={2}>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                            <Grid item xs={12} md={6} key={item}>
                                <FeatureToggleSkeleton />
                            </Grid>
                        ))}
                    </Grid>
                </Card>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: '1px solid #EDEDED' }}>
                <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
            </Box>
        </Box>
    </Box>
);

const FeatureToggleCard = ({ title, description, checked, onChange, locked }) => (
    <Card className={`feature-card ${locked ? 'locked' : ''}`}>
        <Box className="feature-card-content">
            <Box className="feature-info">
                <Box className="feature-title-row">
                    <CustomTypography variant="h6" className="feature-title" label={title} sx={{ mb: 0 }} />
                    {locked && (
                        <Tooltip title="You don’t have permission to access this feature under your current plan. Please contact your administrator for assistance.">
                            <LockIcon className="lock-icon" />
                        </Tooltip>
                    )}
                </Box>
                <CustomTypography variant="body2" className="feature-description" label={description} sx={{ mb: 0 }} />
            </Box>
            <IOSSwitch checked={checked} onChange={onChange} disabled={locked} />
        </Box>
    </Card>
);

const STEPS = [
    {
        id: 0,
        label: "Order & Feature Settings",
        title: "Order & Feature Settings",
        description: "Control which ordering methods and modules are available.",
        icon: <TuneIcon sx={{ fontSize: 19, marginRight:'0px !important' }} />,
    },
    {
        id: 1,
        label: "Booking & Preparation",
        title: "Booking & Preparation",
        description: "Configure booking limits, booking charges, and preparation time.",
        icon: <AccessTimeIcon sx={{ fontSize: 19, marginRight:'0px !important' }} />,
    },
    {
        id: 2,
        label: "Charges & Delivery",
        title: "Charges & Delivery",
        description: "Manage restaurant fees and delivery pricing rules.",
        icon: <LocalShippingIcon sx={{ fontSize: 19, marginRight:'0px !important' }} />,
    },
    {
        id: 3,
        label: "Payments & Hardware",
        title: "Payments & Hardware",
        description: "Configure payment payouts and local printing.",
        icon: <PrintIcon sx={{ fontSize: 19, marginRight:'0px !important' }} />,
    },
];

const ManageSettingsContainer = ({
    formik,
    permissions,
    loading,
    isUpdating,
    onToggleSetting,
    qrCodeUrl,
    isGeneratingQr,
    isVendorQrLoading,
    onGenerateQr,
    onViewQr,
    orderLink: propOrderLink,
    orderQrCode: propOrderQrCode,
    onViewOrderQr,
    stripeAccountId,
    stripeOnboardingCompleted,
    isOnboardingStripe,
    isStripeStatusLoading,
    onStripeOnboard,
    refetchStripeStatus
}) => {
    const [activeTab, setActiveTab] = useState(0);

    const { values, handleChange, setFieldValue, handleSubmit, errors, touched } = formik;
    const posData = useSelector((state) => state?.posData);
    const vendorData = posData?.vendor;
    const storePermissions = useMemo(() => getOrderTypePermissions(posData), [posData]);

    const orderLink = propOrderLink || vendorData?.order_link || posData?.order_link || '';
    const orderQrCode = propOrderQrCode || vendorData?.order_qr_code || posData?.order_qr_code || '';

    const [copied, setCopied] = useState(false);

    const handleCopyLink = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        if (!orderLink) return;
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(orderLink);
        } else {
            const textArea = document.createElement("textarea");
            textArea.value = orderLink;
            textArea.style.position = "fixed";
            textArea.style.left = "-999999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
            } catch (err) {
                console.error('Fallback copy failed', err);
            }
            document.body.removeChild(textArea);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    const effectivePermissions = useMemo(() => {
        let apiPerms = permissions;
        if (typeof apiPerms === 'string') {
            try {
                apiPerms = JSON.parse(apiPerms);
            } catch (e) { }
        }
        return {
            ...storePermissions,
            ...(apiPerms && typeof apiPerms === 'object' ? apiPerms : {})
        };
    }, [permissions, storePermissions]);

    const isSuperAdminCalendarAllowed = Boolean(
        effectivePermissions?.permission_pos_calender_view === 1 ||
        effectivePermissions?.permission_pos_calender_view === '1' ||
        effectivePermissions?.permission_pos_calendar_view === 1 ||
        effectivePermissions?.permission_pos_calendar_view === '1' ||
        effectivePermissions?.permission_pos_calender_view === true ||
        effectivePermissions?.permission_pos_calendar_view === true
    );

    const isSuperAdminInventoryAllowed = Boolean(
        effectivePermissions?.permission_pos_inventory === 1 ||
        effectivePermissions?.permission_pos_inventory === '1' ||
        effectivePermissions?.permission_pos_inventory === true
    );

    const isSuperAdminChefPanelAllowed = Boolean(
        effectivePermissions?.permission_pos_chef_panel === 1 ||
        effectivePermissions?.permission_pos_chef_panel === '1' ||
        effectivePermissions?.permission_pos_chef_panel === true
    );

    const hasAnyPosLevelAccess = isSuperAdminCalendarAllowed || isSuperAdminInventoryAllowed || isSuperAdminChefPanelAllowed;

    const isTimeOutVendor = vendorData?.business_type === 'TimeOut';

    // --- Delivery Tier Helpers ---
    const getFromMilesForTier = (index) => {
        if (index === 0) {
            const freeVal = parseFloat(values.free_delivery_up_to_miles);
            return isNaN(freeVal) ? 0 : freeVal;
        }
        const prevUpTo = parseFloat(values.delivery_tiers[index - 1]?.up_to_miles);
        return isNaN(prevUpTo) ? 0 : prevUpTo;
    };

    const handleAddTier = () => {
        const fromMiles = getFromMilesForTier(values.delivery_tiers.length);
        setFieldValue('delivery_tiers', [
            ...values.delivery_tiers,
            { from_miles: fromMiles, up_to_miles: '', fee: '' },
        ]);
    };

    const handleDeleteTier = (index) => {
        const updatedTiers = values.delivery_tiers.filter((_, i) => i !== index);
        // Recalculate from_miles for each tier after deletion
        const recalculated = updatedTiers.map((tier, i) => ({
            ...tier,
            from_miles: i === 0
                ? (parseFloat(values.free_delivery_up_to_miles) || 0)
                : (parseFloat(updatedTiers[i - 1]?.up_to_miles) || 0),
        }));
        setFieldValue('delivery_tiers', recalculated);
    };

    const handleTierChange = (index, field, value) => {
        const updatedTiers = values.delivery_tiers.map((tier, i) =>
            i === index ? { ...tier, [field]: value } : tier
        );
        // If up_to_miles changed, cascade from_miles to the next tier
        if (field === 'up_to_miles') {
            const parsed = parseFloat(value);
            if (!isNaN(parsed) && updatedTiers[index + 1] !== undefined) {
                updatedTiers[index + 1] = { ...updatedTiers[index + 1], from_miles: parsed };
            }
        }
        setFieldValue('delivery_tiers', updatedTiers);
    };

    // Recalculate all from_miles when free_delivery_up_to_miles changes
    const handleFreeMilesChange = (e) => {
        const val = e.target.value;
        setFieldValue('free_delivery_up_to_miles', val);
        if (values.delivery_tiers.length > 0) {
            const parsed = parseFloat(val);
            const updatedTiers = values.delivery_tiers.map((tier, i) => {
                if (i === 0) return { ...tier, from_miles: isNaN(parsed) ? 0 : parsed };
                return tier;
            });
            setFieldValue('delivery_tiers', updatedTiers);
        }
    };

    // Tab-level error detection to show visual indicators
    const getTabHasError = (index) => {
        if (index === 1) {
            return Boolean(
                (touched.packingBufferTime && errors.packingBufferTime) ||
                (touched.tableBookingPrice && errors.tableBookingPrice) ||
                (touched.max_guest_count && errors.max_guest_count)
            );
        }
        if (index === 2) {
            return Boolean(
                (touched.package_charge_price && errors.package_charge_price) ||
                (touched.free_delivery_up_to_miles && errors.free_delivery_up_to_miles) ||
                (touched.delivery_tiers && errors.delivery_tiers)
            );
        }
        if (index === 3) {
            return Boolean(
                (touched.printer_ip && errors.printer_ip) ||
                (touched.printer_port && errors.printer_port)
            );
        }
        return false;
    };

    // Save handler: auto-navigates to step with errors if validation fails
    const handleSave = async (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        const validationErrors = await formik.validateForm();
        if (validationErrors && Object.keys(validationErrors).length > 0) {
            if (validationErrors.packingBufferTime || validationErrors.tableBookingPrice || validationErrors.max_guest_count) {
                setActiveTab(1);
            } else if (validationErrors.package_charge_price || validationErrors.free_delivery_up_to_miles || validationErrors.delivery_tiers) {
                setActiveTab(2);
            } else if (validationErrors.printer_ip || validationErrors.printer_port) {
                setActiveTab(3);
            }
        }
        handleSubmit();
    };

    return (
        <MainContainer bgcolor={"#fff"}>
            {loading ? (
                <SettingsSkeleton />
            ) : (
                <Box className="manage-settings-page">
                    {/* Header */}
                    <Box className="settings-header">
                        <Box className="settings-header-left">
                            <CustomTypography
                                label={"Manage Settings"}
                                variant={"h5"}
                                sx={{
                                    fontSize: '24px', fontWeight: 600,
                                    color: theme.palette.typographyLabel.main, mb: 0
                                }}
                            />
                            <CustomTypography
                                variant="body2"
                                label={"Configure your POS features and preferences"}
                                sx={{
                                    textAlign: 'left', mb: 0,
                                    fontSize: '16px', fontWeight: 400, color: theme.palette.lightgrey.main
                                }}
                            />
                        </Box>

                        {/* Right header actions: Order Link & QR + TimeOut QR */}
                        <Box className="settings-header-actions">
                            {(orderLink || orderQrCode) && (
                                <Box className="order-link-pill-container">
                                    {orderLink && (
                                        <>
                                            <Box className="order-link-badge">
                                                <LinkIcon sx={{ fontSize: 17, color: theme.palette.primary.main }} />
                                                <span className="order-link-badge-text">Order Link:</span>
                                            </Box>
                                            <Tooltip title={orderLink} arrow placement="top">
                                                <a
                                                    href={orderLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="order-link-anchor"
                                                >
                                                    {orderLink}
                                                </a>
                                            </Tooltip>
                                            <Tooltip title={copied ? "Copied!" : "Copy link"} arrow placement="top">
                                                <IconButton
                                                    size="small"
                                                    onClick={handleCopyLink}
                                                    className="order-link-copy-icon-btn"
                                                    aria-label="Copy order link"
                                                >
                                                    {copied ? (
                                                        <CheckIcon sx={{ fontSize: 16, color: '#10B981' }} />
                                                    ) : (
                                                        <ContentCopyIcon sx={{ fontSize: 16, color: '#4B5563' }} />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}

                                    {orderQrCode && (
                                        <CustomButton
                                            variant="outlined"
                                            startIcon={<QrCodeIcon sx={{ fontSize: 18 }} />}
                                            onClick={onViewOrderQr}
                                            className="order-qr-view-button"
                                        >
                                            View QR
                                        </CustomButton>
                                    )}
                                </Box>
                            )}

                            {isTimeOutVendor && (
                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                    {qrCodeUrl ? (
                                        <CustomButton
                                            variant="contained"
                                            startIcon={<VisibilityIcon />}
                                            onClick={onViewQr}
                                            disabled={isVendorQrLoading}
                                            className="save-button"
                                        >
                                            View QR
                                        </CustomButton>
                                    ) : (
                                        <CustomButton
                                            variant="contained"
                                            startIcon={<QrCodeIcon />}
                                            onClick={onGenerateQr}
                                            disabled={isGeneratingQr || isVendorQrLoading}
                                            className="save-button"
                                        >
                                            {isGeneratingQr ? "Generating..." : "Generate QR Code"}
                                        </CustomButton>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Horizontal Steps Tabs (as per Image 2 reference) */}
                    <Box className="settings-tabs-container">
                        <Tabs
                            value={activeTab}
                            onChange={(_, newValue) => setActiveTab(newValue)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {STEPS.map((step) => {
                                const hasError = getTabHasError(step.id);
                                return (
                                    <Tab
                                        key={step.id}
                                        value={step.id}
                                        icon={step.icon}
                                        iconPosition="start"
                                        disableRipple
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <span>{step.label}</span>
                                                {hasError && (
                                                    <Box
                                                        sx={{
                                                            width: 7,
                                                            height: 7,
                                                            borderRadius: '50%',
                                                            bgcolor: '#EF4444',
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        }
                                    />
                                );
                            })}
                        </Tabs>
                    </Box>

                    {/* Step Content */}
                    <Box className="settings-content">
                        {/* ═══════════════ STEP 1: Order & Feature Settings ═══════════════ */}
                        {activeTab === 0 && (
                            <Box className="step-content">


                                <Box className="section-wrapper">
                                    <Card className="settings-section-card">
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <FeatureToggleCard
                                                    title="Takeaway Orders"
                                                    description="Allow customers to place takeaway orders"
                                                    checked={values.takeAway}
                                                    onChange={(e) => (onToggleSetting ? onToggleSetting('takeAway', e.target.checked) : setFieldValue('takeAway', e.target.checked))}
                                                    locked={!isPermissionAllowed(effectivePermissions, 'permission_take_away')}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FeatureToggleCard
                                                    title="Delivery Orders"
                                                    description="Enable delivery order functionality"
                                                    checked={values.delivery}
                                                    onChange={(e) => (onToggleSetting ? onToggleSetting('delivery', e.target.checked) : setFieldValue('delivery', e.target.checked))}
                                                    locked={!isPermissionAllowed(effectivePermissions, 'permission_delivery')}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FeatureToggleCard
                                                    title="Table Booking Flow"
                                                    description="Allow customers to book tables in advance"
                                                    checked={values.tableBookingFlow}
                                                    onChange={(e) => (onToggleSetting ? onToggleSetting('tableBookingFlow', e.target.checked) : setFieldValue('tableBookingFlow', e.target.checked))}
                                                    locked={!isPermissionAllowed(effectivePermissions, 'permission_table_booking')}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FeatureToggleCard
                                                    title="Collection Points"
                                                    description="Allow customers to pick up from designated points"
                                                    checked={values.collectionPoints}
                                                    onChange={(e) => (onToggleSetting ? onToggleSetting('collectionPoints', e.target.checked) : setFieldValue('collectionPoints', e.target.checked))}
                                                    locked={!isPermissionAllowed(effectivePermissions, 'permission_collection_point')}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FeatureToggleCard
                                                    title="E-Menu User Login"
                                                    description="Allow customers to browse menu via QR code"
                                                    checked={values.eMenuUserLogin}
                                                    onChange={(e) => (onToggleSetting ? onToggleSetting('eMenuUserLogin', e.target.checked) : setFieldValue('eMenuUserLogin', e.target.checked))}
                                                    locked={!isPermissionAllowed(effectivePermissions, 'permission_pos_e_menu')}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FeatureToggleCard
                                                    title="Drive Through"
                                                    description="Enable drive-through ordering for customers"
                                                    checked={values.driveThrough}
                                                    onChange={(e) => (onToggleSetting ? onToggleSetting('driveThrough', e.target.checked) : setFieldValue('driveThrough', e.target.checked))}
                                                    locked={!isPermissionAllowed(effectivePermissions, 'permission_drive_thru')}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FeatureToggleCard
                                                    title="Chef User Login"
                                                    description="Enable separate login for kitchen staff"
                                                    checked={values.chefUserLogin}
                                                    onChange={(e) => (onToggleSetting ? onToggleSetting('chefUserLogin', e.target.checked) : setFieldValue('chefUserLogin', e.target.checked))}
                                                    locked={!isPermissionAllowed(effectivePermissions, 'permission_pos_chef_panel')}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FeatureToggleCard
                                                    title="Mandatory customer details for dine-in orders"
                                                    description="Require customer details for dine-in orders"
                                                    checked={values.mandatoryCustomerDetailsDineIn}
                                                    onChange={(e) => (onToggleSetting ? onToggleSetting('mandatoryCustomerDetailsDineIn', e.target.checked) : setFieldValue('mandatoryCustomerDetailsDineIn', e.target.checked))}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Card>
                                </Box>

                                {/* ══════════ POS Level Access Section ══════════ */}
                                {hasAnyPosLevelAccess && (
                                    <Box className="section-wrapper" sx={{ mt: 3 }}>
                                        <Box sx={{ mb: 2 }}>
                                            <CustomTypography
                                                variant="h6"
                                                className="pos-access-section-title"
                                                label="POS Level Configurations"
                                                sx={{ mb: 0.5, fontWeight: 600, fontSize: '18px' }}
                                            />
                                            <CustomTypography
                                                variant="body2"
                                                className="pos-access-section-subtitle"
                                                label="Enable or disable access to specific screens in your POS terminal. Disabling a screen will remove its access from your system."
                                                sx={{ color: '#6B7280', fontSize: '13px', mb: 0 }}
                                            />
                                        </Box>
                                        <Card className="settings-section-card">
                                            <Grid container spacing={2}>
                                                {isSuperAdminCalendarAllowed && (
                                                    <Grid item xs={12} md={6}>
                                                        <FeatureToggleCard
                                                            title="Calendar Screen"
                                                            description={
                                                                values.calendar
                                                                    ? "Calendar screen is accessible in POS. Turn off to remove access from your system."
                                                                    : "Calendar screen is disabled and access is removed from your POS system."
                                                            }
                                                            checked={Boolean(values.calendar)}
                                                            onChange={(e) => (onToggleSetting ? onToggleSetting('calendar', e.target.checked) : setFieldValue('calendar', e.target.checked))}
                                                        />
                                                    </Grid>
                                                )}
                                                {isSuperAdminInventoryAllowed && (
                                                    <Grid item xs={12} md={6}>
                                                        <FeatureToggleCard
                                                            title="Inventory Screen"
                                                            description={
                                                                values.inventory
                                                                    ? "Inventory screen is accessible in POS. Turn off to remove access from your system."
                                                                    : "Inventory screen is disabled and access is removed from your POS system."
                                                            }
                                                            checked={Boolean(values.inventory)}
                                                            onChange={(e) => (onToggleSetting ? onToggleSetting('inventory', e.target.checked) : setFieldValue('inventory', e.target.checked))}
                                                        />
                                                    </Grid>
                                                )}
                                                {isSuperAdminChefPanelAllowed && (
                                                    <Grid item xs={12} md={6}>
                                                        <FeatureToggleCard
                                                            title="Serve Panel"
                                                            description={
                                                                values.serve_panel
                                                                    ? "Kitchen serve panel is accessible in POS. Turn off to remove access from your system."
                                                                    : "Kitchen serve panel is disabled and access is removed from your POS system."
                                                            }
                                                            checked={Boolean(values.serve_panel)}
                                                            onChange={(e) => (onToggleSetting ? onToggleSetting('serve_panel', e.target.checked) : setFieldValue('serve_panel', e.target.checked))}
                                                        />
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Card>
                                    </Box>
                                )}

                                <Box className="settings-footer-note" sx={{ mt: 3 }}>
                                    <CustomTypography
                                        variant="body2"
                                        label={
                                            <>
                                                <strong>Note:</strong> Settings marked with a lock icon are controlled by your Super Admin. Contact your administrator to request changes to these settings.
                                            </>
                                        }
                                        sx={{ mb: 0 }}
                                    />
                                </Box>
                            </Box>
                        )}

                        {/* ═══════════════ STEP 2: Booking & Preparation ═══════════════ */}
                        {activeTab === 1 && (
                            <Box className="step-content">
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Card className="config-card">
                                            <CustomTypography
                                                variant="h6"
                                                className="config-title"
                                                label={"Packing Buffer Time"}
                                                sx={{ fontSize: '18px', fontWeight: 600, mb: 2 }}
                                            />
                                            <Box className="config-field-wrapper">
                                                <CustomTypography
                                                    variant="subtitle2"
                                                    className="field-label"
                                                    label={"Buffer Time (minutes)"}
                                                    sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }}
                                                />
                                                <CustomTextField
                                                    fullWidth
                                                    name="packingBufferTime"
                                                    value={values.packingBufferTime}
                                                    onChange={handleChange}
                                                    placeholder="15"
                                                    className="config-input"
                                                    error={touched.packingBufferTime && Boolean(errors.packingBufferTime)}
                                                    helperText={
                                                        touched.packingBufferTime && errors.packingBufferTime
                                                            ? errors.packingBufferTime
                                                            : "Extra time added per food item for packing (0-120 minutes)"
                                                    }
                                                />
                                            </Box>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Card className="config-card">
                                            <CustomTypography
                                                variant="h6"
                                                className="config-title"
                                                label={"Table Booking"}
                                                sx={{ fontSize: '18px', fontWeight: 600, mb: 2 }}
                                            />
                                            <Box className="config-field-wrapper">
                                                <CustomTypography
                                                    variant="subtitle2"
                                                    className="field-label"
                                                    label={`Booking Price (${getCurrencySymbol(vendorData?.currency)})`}
                                                    sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }}
                                                />
                                                <CustomTextField
                                                    fullWidth
                                                    name="tableBookingPrice"
                                                    value={values.tableBookingPrice}
                                                    onChange={handleChange}
                                                    placeholder="50"
                                                    className="config-input"
                                                    error={touched.tableBookingPrice && Boolean(errors.tableBookingPrice)}
                                                    helperText={
                                                        touched.tableBookingPrice && errors.tableBookingPrice
                                                            ? errors.tableBookingPrice
                                                            : "Price charged for table booking"
                                                    }
                                                />
                                            </Box>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Card className="config-card">
                                            <CustomTypography
                                                variant="h6"
                                                className="config-title"
                                                label={"Max Table Booking Guest Count"}
                                                sx={{ fontSize: '18px', fontWeight: 600, mb: 2 }}
                                            />
                                            <Box className="config-field-wrapper">
                                                <CustomTypography
                                                    variant="subtitle2"
                                                    className="field-label"
                                                    label={`Max Guest Count`}
                                                    sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }}
                                                />
                                                <CustomTextField
                                                    fullWidth
                                                    name="max_guest_count"
                                                    value={values.max_guest_count}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                                                        setFieldValue('max_guest_count', value);
                                                    }}
                                                    placeholder="Enter Max Guest Count"
                                                    className="config-input"
                                                    error={touched.max_guest_count && Boolean(errors.max_guest_count)}
                                                    helperText={
                                                        touched.max_guest_count && errors.max_guest_count
                                                            ? errors.max_guest_count
                                                            : "Max Table Booking Guest Count For Customer Side"
                                                    }
                                                />
                                            </Box>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* ═══════════════ STEP 3: Charges & Delivery ═══════════════ */}
                        {activeTab === 2 && (
                            <Box className="step-content">
                                <Grid container spacing={3}>
                                    {/* ── Restaurant Package Charge ── */}
                                    <Grid item xs={12}>
                                        <Card className="config-card">
                                            <Box className="delivery-card-header">
                                                <Box className="delivery-card-icon-wrap">
                                                    <InventoryIcon sx={{ fontSize: 22, color: '#555' }} />
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <CustomTypography
                                                        variant="h6"
                                                        label={"Restaurant Package Charge"}
                                                        sx={{ fontSize: '16px', fontWeight: 600, mb: 0 }}
                                                    />
                                                    <CustomTypography
                                                        variant="body2"
                                                        label={"Apply a packaging fee to every order"}
                                                        sx={{ fontSize: '13px', color: theme.palette.lightgrey.main, mb: 0 }}
                                                    />
                                                </Box>
                                                <IOSSwitch
                                                    checked={values.package_charge_enabled}
                                                    onChange={(e) => setFieldValue('package_charge_enabled', e.target.checked)}
                                                />
                                            </Box>

                                            {values.package_charge_enabled && (
                                                <Box sx={{ mt: 2 }}>
                                                    <CustomTypography
                                                        variant="subtitle2"
                                                        label={`Package Charge (${getCurrencySymbol(vendorData?.currency)})`}
                                                        sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }}
                                                    />
                                                    <CustomTextField
                                                        fullWidth
                                                        name="package_charge_price"
                                                        inputProps={{ inputMode: 'decimal' }}
                                                        value={values.package_charge_price}
                                                        onChange={(e) => {
                                                            const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                                                            setFieldValue('package_charge_price', val);
                                                        }}
                                                        placeholder="0.00"
                                                        className="config-input"
                                                        sx={{ maxWidth: 500 }}
                                                        error={touched.package_charge_price && Boolean(errors.package_charge_price)}
                                                        helperText={touched.package_charge_price && errors.package_charge_price}
                                                    />
                                                </Box>
                                            )}
                                        </Card>
                                    </Grid>

                                    {/* ── Delivery Fee by Radius ── */}
                                    <Grid item xs={12}>
                                        <Card className="config-card">
                                            <Box className="delivery-card-header">
                                                <Box className="delivery-card-icon-wrap">
                                                    <LocalShippingIcon sx={{ fontSize: 22, color: '#555' }} />
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <CustomTypography
                                                        variant="h6"
                                                        label={"Delivery Fee by Radius"}
                                                        sx={{ fontSize: '16px', fontWeight: 600, mb: 0 }}
                                                    />
                                                    <CustomTypography
                                                        variant="body2"
                                                        label={"Configure free delivery zone and per-mile fee tiers"}
                                                        sx={{ fontSize: '13px', color: theme.palette.lightgrey.main, mb: 0 }}
                                                    />
                                                </Box>
                                            </Box>

                                            {/* Free Delivery Miles */}
                                            <Box className="delivery-zone-box" sx={{ mt: 2 }}>
                                                <CustomTypography
                                                    variant="subtitle2"
                                                    label={"Free Delivery Up To (miles)"}
                                                    sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }}
                                                />
                                                <CustomTextField
                                                    fullWidth
                                                    name="free_delivery_up_to_miles"
                                                    inputProps={{ inputMode: 'decimal' }}
                                                    value={values.free_delivery_up_to_miles}
                                                    onChange={(e) => {
                                                        const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                                                        handleFreeMilesChange({ target: { value: val } });
                                                    }}
                                                    placeholder="e.g. 2"
                                                    className="config-input"
                                                    sx={{ maxWidth: 500 }}
                                                    error={touched.free_delivery_up_to_miles && Boolean(errors.free_delivery_up_to_miles)}
                                                    helperText={
                                                        touched.free_delivery_up_to_miles && errors.free_delivery_up_to_miles
                                                            ? errors.free_delivery_up_to_miles
                                                            : 'No delivery fee within this radius'
                                                    }
                                                />
                                            </Box>

                                            {/* Tier Table Header */}
                                            <Box className="tiers-header-row" sx={{ mt: 3 }}>
                                                <CustomTypography
                                                    variant="subtitle2"
                                                    label={"Distance-Based Fee Tiers"}
                                                    sx={{ fontSize: '14px', fontWeight: 600, mb: 0 }}
                                                />
                                                <CustomButton
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    onClick={handleAddTier}
                                                    sx={{ textTransform: 'none', fontSize: '13px', borderRadius: '8px' }}
                                                >
                                                    Add Tier
                                                </CustomButton>
                                            </Box>

                                            {/* Tier-level validation error */}
                                            {typeof errors.delivery_tiers === 'string' && (
                                                <CustomTypography
                                                    variant="caption"
                                                    label={errors.delivery_tiers}
                                                    sx={{ color: 'error.main', mt: 0.5 }}
                                                />
                                            )}

                                            {/* Tiers List */}
                                            {values.delivery_tiers.length > 0 && (
                                                <Box sx={{ mt: 1 }}>
                                                    {values.delivery_tiers.map((tier, index) => {
                                                        const tierErrors = errors.delivery_tiers?.[index] || {};
                                                        const tierTouched = touched.delivery_tiers?.[index] || {};
                                                        return (
                                                            <Box key={index} className="tier-row">
                                                                {/* From (mi) — read-only */}
                                                                <Box className="tier-field-wrap">
                                                                    <CustomTypography
                                                                        variant="caption"
                                                                        label={"From (mi)"}
                                                                        sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: 0.5 }}
                                                                    />
                                                                    <CustomTextField
                                                                        fullWidth
                                                                        value={getFromMilesForTier(index)}
                                                                        disabled
                                                                        className="config-input tier-input-disabled"
                                                                        placeholder="0"
                                                                    />
                                                                </Box>

                                                                {/* Up To (mi) */}
                                                                <Box className="tier-field-wrap">
                                                                    <CustomTypography
                                                                        variant="caption"
                                                                        label={"Up To (mi)"}
                                                                        sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: 0.5 }}
                                                                    />
                                                                    <CustomTextField
                                                                        fullWidth
                                                                        inputProps={{ inputMode: 'decimal' }}
                                                                        value={tier.up_to_miles}
                                                                        onChange={(e) => {
                                                                            const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                                                                            handleTierChange(index, 'up_to_miles', val);
                                                                        }}
                                                                        placeholder="e.g. 5"
                                                                        className="config-input"
                                                                        error={tierTouched.up_to_miles && Boolean(tierErrors.up_to_miles)}
                                                                        helperText={tierTouched.up_to_miles && tierErrors.up_to_miles}
                                                                    />
                                                                </Box>

                                                                {/* Fee ($) */}
                                                                <Box className="tier-field-wrap">
                                                                    <CustomTypography
                                                                        variant="caption"
                                                                        label={`Fee (${getCurrencySymbol(vendorData?.currency)})`}
                                                                        sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: 0.5 }}
                                                                    />
                                                                    <CustomTextField
                                                                        fullWidth
                                                                        inputProps={{ inputMode: 'decimal' }}
                                                                        value={tier.fee}
                                                                        onChange={(e) => {
                                                                            const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                                                                            handleTierChange(index, 'fee', val);
                                                                        }}
                                                                        placeholder="0.00"
                                                                        className="config-input"
                                                                        error={tierTouched.fee && Boolean(tierErrors.fee)}
                                                                        helperText={tierTouched.fee && tierErrors.fee}
                                                                    />
                                                                </Box>

                                                                {/* Delete */}
                                                                <IconButton
                                                                    onClick={() => handleDeleteTier(index)}
                                                                    className="tier-delete-btn"
                                                                    size="small"
                                                                >
                                                                    <DeleteOutlineIcon sx={{ color: '#E53935', fontSize: 20 }} />
                                                                </IconButton>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            )}

                                            {values.delivery_tiers.length === 0 && (
                                                <Box className="tiers-empty-state">
                                                    <CustomTypography
                                                        variant="body2"
                                                        label={"No tiers added yet. Click \"+ Add Tier\" to create distance-based delivery fees."}
                                                        sx={{ color: theme.palette.lightgrey.main, fontSize: '13px' }}
                                                    />
                                                </Box>
                                            )}
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* ═══════════════ STEP 4: Payments & Hardware ═══════════════ */}
                        {activeTab === 3 && (
                            <Box className="step-content">
                                <Grid container spacing={3}>
                                    {/* ── Stripe Connect Setup ── */}
                                    <Grid item xs={12}>
                                        <Card className="config-card">
                                            <Box className="delivery-card-header">
                                                <Box className="delivery-card-icon-wrap">
                                                    <AccountBalanceIcon sx={{ fontSize: 22, color: '#635BFF' }} />
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <CustomTypography
                                                        variant="h6"
                                                        label={"Stripe Connect Payout Setup"}
                                                        sx={{ fontSize: '18px', fontWeight: 600, mb: 0 }}
                                                    />
                                                    <CustomTypography
                                                        variant="body2"
                                                        label={"Connect your bank account to receive direct online order payouts."}
                                                        sx={{ fontSize: '13px', color: theme.palette.lightgrey.main, mb: 0 }}
                                                    />
                                                </Box>
                                            </Box>

                                            <Box sx={{ mt: 2 }}>
                                                {stripeOnboardingCompleted ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderRadius: 1, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9' }}>
                                                        <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 24 }} />
                                                        <Box sx={{ flex: 1 }}>
                                                            <CustomTypography
                                                                variant="subtitle2"
                                                                label={"Stripe Account Connected & Verified"}
                                                                sx={{ fontSize: '14px', fontWeight: 600, color: '#2e7d32', mb: 0 }}
                                                            />
                                                            <CustomTypography
                                                                variant="caption"
                                                                label={`Account ID: ${stripeAccountId}`}
                                                                sx={{ fontSize: '12px', color: '#388e3c', mb: 0 }}
                                                            />
                                                        </Box>
                                                        <IconButton size="small" onClick={refetchStripeStatus} disabled={isStripeStatusLoading} title="Refresh Status">
                                                            <RefreshIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                                                        </IconButton>
                                                    </Box>
                                                ) : stripeAccountId ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fff8e1', border: '1px solid #ffe082' }}>
                                                            <CustomTypography
                                                                variant="subtitle2"
                                                                label={"Onboarding Pending"}
                                                                sx={{ fontSize: '14px', fontWeight: 600, color: '#b78103', mb: 0.5 }}
                                                            />
                                                            <CustomTypography
                                                                variant="body2"
                                                                label={`Account ID (${stripeAccountId}) created. Please complete onboarding with Stripe to receive payouts.`}
                                                                sx={{ fontSize: '12px', color: '#795548', mb: 0 }}
                                                            />
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <CustomButton
                                                                variant="contained"
                                                                onClick={onStripeOnboard}
                                                                disabled={isOnboardingStripe}
                                                                sx={{ bgcolor: '#635BFF', '&:hover': { bgcolor: '#0A2540' }, textTransform: 'none', borderRadius:'8px' }}
                                                            >
                                                                {isOnboardingStripe ? "Redirecting..." : "Complete Onboarding"}
                                                            </CustomButton>
                                                            <CustomButton
                                                                variant="outlined"
                                                                onClick={refetchStripeStatus}
                                                                disabled={isStripeStatusLoading}
                                                                startIcon={<RefreshIcon />}
                                                                sx={{ textTransform: 'none', borderRadius:'8px' }}
                                                            >
                                                                Verify Status
                                                            </CustomButton>
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                        <CustomTypography
                                                            variant="body2"
                                                            label={"Create or connect your Stripe Express account to start taking online payment transfers directly into your bank account."}
                                                            sx={{ fontSize: '13px', color: theme.palette.lightgrey.main, mb: 1 }}
                                                        />
                                                        <CustomButton
                                                            variant="contained"
                                                            onClick={onStripeOnboard}
                                                            disabled={isOnboardingStripe}
                                                            sx={{ bgcolor: '#635BFF', '&:hover': { bgcolor: '#0A2540' }, textTransform: 'none', alignSelf: 'flex-start' }}
                                                        >
                                                            {isOnboardingStripe ? "Connecting..." : "Connect with Stripe"}
                                                        </CustomButton>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Card>
                                    </Grid>

                                    {/* ── Network Thermal Printer Settings ── */}
                                    <Grid item xs={12}>
                                        <Card className="config-card">
                                            <Box className="delivery-card-header">
                                                <Box className="delivery-card-icon-wrap">
                                                    <PrintIcon sx={{ fontSize: 22, color: '#555' }} />
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <CustomTypography
                                                        variant="h6"
                                                        label={"Network Thermal Printer Settings (LAN / Wi-Fi)"}
                                                        sx={{ fontSize: '16px', fontWeight: 600, mb: 0 }}
                                                    />
                                                    <CustomTypography
                                                        variant="body2"
                                                        label={"Automatically print receipts on thermal network printer when QR order is received"}
                                                        sx={{ fontSize: '13px', color: theme.palette.lightgrey.main, mb: 0 }}
                                                    />
                                                </Box>
                                                <IOSSwitch
                                                    checked={values.auto_print_qr_orders}
                                                    onChange={(e) => setFieldValue('auto_print_qr_orders', e.target.checked)}
                                                />
                                            </Box>

                                            {values.auto_print_qr_orders && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={8}>
                                                            <CustomTypography
                                                                variant="subtitle2"
                                                                label={"Printer Network IP Address"}
                                                                sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }}
                                                            />
                                                            <CustomTextField
                                                                fullWidth
                                                                name="printer_ip"
                                                                value={values.printer_ip}
                                                                onChange={handleChange}
                                                                placeholder="e.g. 192.168.1.200"
                                                                className="config-input"
                                                                error={touched.printer_ip && Boolean(errors.printer_ip)}
                                                                helperText={
                                                                    touched.printer_ip && errors.printer_ip
                                                                        ? errors.printer_ip
                                                                        : "IPv4 Address of the thermal printer connected to the restaurant's local network"
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <CustomTypography
                                                                variant="subtitle2"
                                                                label={"Port"}
                                                                sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }}
                                                            />
                                                            <CustomTextField
                                                                fullWidth
                                                                name="printer_port"
                                                                value={values.printer_port}
                                                                onChange={handleChange}
                                                                placeholder="e.g. 9100"
                                                                className="config-input"
                                                                error={touched.printer_port && Boolean(errors.printer_port)}
                                                                helperText={touched.printer_port && errors.printer_port}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            )}
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* ═══════════════ BOTTOM ACTION BAR: ONLY "SAVE" BUTTON (Steps 2, 3, 4) ═══════════════ */}
                        {activeTab !== 0 && (
                            <Box className="settings-bottom-actions">
                                <CustomButton
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    className="save-button"
                                    onClick={handleSave}
                                    loading={isUpdating}
                                >
                                    Save
                                </CustomButton>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
        </MainContainer>
    );
};

export default ManageSettingsContainer;
